import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useCompanyList } from '../../stores/companyStore';
import { Company } from '../../types';

const GLOBAL_OPTION_ID = 'global';
const GLOBAL_OPTION_LABEL = 'Global (todas as empresas)';

interface GlobalOption {
  id: typeof GLOBAL_OPTION_ID;
  legalName: string;
  tradeName?: string;
}

type CompanyOrGlobal = Company | GlobalOption;

interface CompanySelectionModalProps {
  open: boolean;
  onSelect: (companyId: string, companyName: string) => void;
  allowGlobal?: boolean;
}

export const CompanySelectionModal: React.FC<CompanySelectionModalProps> = ({
  open,
  onSelect,
  allowGlobal = false,
}) => {
  const { companies, carregada, isLoading, loadCompanies } = useCompanyList();
  const [selected, setSelected] = useState<CompanyOrGlobal | null>(null);

  // Busca enquanto não houver resposta para este contexto, e não enquanto a lista estiver
  // vazia: vazia é uma resposta possível, e reperguntá-la não muda nada.
  useEffect(() => {
    if (open && !carregada && !isLoading) {
      loadCompanies();
    }
  }, [open, carregada, isLoading, loadCompanies]);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelected(null);
    }
  }, [open]);

  const options: CompanyOrGlobal[] = allowGlobal
    ? [{ id: GLOBAL_OPTION_ID, legalName: GLOBAL_OPTION_LABEL }, ...companies]
    : companies;

  /**
   * Com uma opção só o diálogo não é uma pergunta: é um obstáculo entre a pessoa e o
   * formulário, com uma linha para clicar e um botão para confirmá-la. Ele se responde
   * sozinho e não chega a aparecer.
   *
   * "Global" conta como opção — quando ela existe há duas, e a escolha volta a ser real.
   */
  const opcaoUnica = carregada && options.length === 1 ? options[0] : undefined;

  // O pai não precisa memorizar `onSelect` para a tela funcionar; sem esta trava, a
  // identidade nova a cada render reexecutaria o efeito e o formulário receberia a mesma
  // empresa em laço.
  const jaRespondeu = useRef(false);
  useEffect(() => {
    if (!open) {
      jaRespondeu.current = false;
      return;
    }
    if (!opcaoUnica || opcaoUnica.id === GLOBAL_OPTION_ID || jaRespondeu.current) return;
    jaRespondeu.current = true;
    const empresa = opcaoUnica as Company;
    onSelect(empresa.id, empresa.tradeName || empresa.legalName || '');
  }, [open, opcaoUnica, onSelect]);

  // Antes da resposta o diálogo não abre: abri-lo com a lista vazia para fechá-lo assim que
  // ela chega é um pisca no meio do caminho de quem só quer preencher o formulário.
  if (open && (!carregada || (opcaoUnica && opcaoUnica.id !== GLOBAL_OPTION_ID))) {
    return null;
  }

  const handleConfirm = () => {
    if (!selected) return;
    if (selected.id === GLOBAL_OPTION_ID) {
      onSelect(GLOBAL_OPTION_ID, GLOBAL_OPTION_LABEL);
    } else {
      const company = selected as Company;
      const displayName = company.tradeName || company.legalName || '';
      onSelect(company.id, displayName);
    }
  };

  const getOptionLabel = (option: CompanyOrGlobal): string => {
    if (option.id === GLOBAL_OPTION_ID) return GLOBAL_OPTION_LABEL;
    const company = option as Company;
    return company.tradeName || company.legalName || '';
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: { onClick: (e: React.MouseEvent) => e.stopPropagation() },
      }}
    >
      <DialogTitle>Selecione a empresa</DialogTitle>
      <DialogContent>
        <Autocomplete<CompanyOrGlobal>
          options={options}
          getOptionLabel={getOptionLabel}
          loading={isLoading}
          value={selected}
          onChange={(_, val) => setSelected(val)}
          isOptionEqualToValue={(option, val) => option.id === val?.id}
          sx={{ mt: 1 }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.id === GLOBAL_OPTION_ID ? (
                <Typography fontWeight="bold">{GLOBAL_OPTION_LABEL}</Typography>
              ) : (
                getOptionLabel(option)
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Empresa"
              placeholder="Digite para buscar..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selected}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
